/**
 * TenantManagement — Multi-tenant admin view.
 * L7/ADMIN only. Lists all organizations and creates new tenants with
 * a bootstrap admin user (email + password) scoped to the new org.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import PageLayout from '../components/ui/PageLayout';
import DataTable, { Column } from '../components/ui/DataTable';

interface Org {
  id: number;
  name: string;
  org_code: string | null;
  tier: string | null;
  default_currency: string | null;
  default_framework: string | null;
  country: string | null;
  onboarding_status: string | null;
  is_active: boolean;
  created_at: string | null;
}

const initialForm = {
  org_name: '',
  org_code: '',
  country: 'US',
  default_currency: 'USD',
  default_framework: 'US_GAAP',
  default_timezone: 'UTC',
  tier: 'FREE',
  primary_contact_email: '',
  primary_contact_name: '',
  admin_email: '',
  admin_password: '',
  admin_full_name: '',
};

export default function TenantManagement() {
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    api.get('/tenants')
      .then(r => setOrgs(r.data.organizations || []))
      .catch(err => {
        const status = err.response?.status;
        if (status === 403) setLoadError('L7 (admin) role required to manage tenants.');
        else setLoadError(err.response?.data?.detail || 'Failed to load tenants');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleField = (key: keyof typeof initialForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [key]: e.target.value }));
  };

  const submit = async () => {
    setFormError(null);
    setSuccessMsg(null);
    if (!form.org_name.trim()) { setFormError('Organization name is required'); return; }
    if (form.admin_email && form.admin_password.length < 8) {
      setFormError('Admin password must be at least 8 characters'); return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        org_name: form.org_name.trim(),
        country: form.country,
        default_currency: form.default_currency,
        default_framework: form.default_framework,
        default_timezone: form.default_timezone,
        tier: form.tier,
      };
      if (form.org_code.trim()) payload.org_code = form.org_code.trim();
      if (form.primary_contact_email.trim()) payload.primary_contact_email = form.primary_contact_email.trim();
      if (form.primary_contact_name.trim()) payload.primary_contact_name = form.primary_contact_name.trim();
      if (form.admin_email.trim()) {
        payload.admin_email = form.admin_email.trim();
        payload.admin_password = form.admin_password;
        if (form.admin_full_name.trim()) payload.admin_full_name = form.admin_full_name.trim();
      }
      const res = await api.post('/tenants', payload);
      const newOrgId = res.data?.id;
      const adminCreated = res.data?.admin_user?.email;
      setSuccessMsg(
        adminCreated
          ? `Tenant #${newOrgId} created. Admin user ${adminCreated} can log in now.`
          : `Tenant #${newOrgId} created. Add an admin user before logging in.`
      );
      setForm(initialForm);
      setShowForm(false);
      load();
    } catch (err: any) {
      const status = err.response?.status;
      if (status === 403) setFormError('L7 (admin) role required.');
      else if (status === 409) setFormError(err.response?.data?.detail || 'Conflict: org_code or email already exists');
      else setFormError(err.response?.data?.detail || 'Failed to create tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const columns: Column[] = [
    { key: 'id', label: 'ID', width: 60 },
    { key: 'name', label: 'Name' },
    { key: 'org_code', label: 'Code', width: 100 },
    { key: 'tier', label: 'Tier', width: 100 },
    { key: 'country', label: 'Country', width: 80 },
    { key: 'default_currency', label: 'Currency', width: 90 },
    { key: 'default_framework', label: 'Framework', width: 110 },
    { key: 'onboarding_status', label: 'Onboarding', width: 120 },
    {
      key: 'is_active',
      label: 'Active',
      width: 80,
      render: (v: boolean) => (
        <span style={{ color: v ? 'var(--green)' : 'var(--text-hint)', fontWeight: 600 }}>
          {v ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      width: 110,
      render: (v: string | null) => v ? new Date(v).toLocaleDateString() : '-',
    },
  ];

  return (
    <PageLayout
      title="Tenant Management"
      subtitle="Create and manage isolated organizations. L7 admin only."
      action={{ label: showForm ? 'Cancel' : 'New Tenant', onClick: () => { setShowForm(s => !s); setFormError(null); setSuccessMsg(null); } }}
    >
      {successMsg && (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 6, background: 'rgba(16,185,129,0.1)', border: '1px solid var(--green)', color: 'var(--green)', fontSize: 13 }}>
          {successMsg}
        </div>
      )}
      {loadError && (
        <div style={{ padding: 12, marginBottom: 16, borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 13 }}>
          {loadError}
        </div>
      )}

      {showForm && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: 'var(--text-primary)' }}>
            Create New Tenant
          </div>
          {formError && (
            <div style={{ padding: 10, marginBottom: 12, borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--red)', color: 'var(--red)', fontSize: 12 }}>
              {formError}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            <Field label="Organization Name *" value={form.org_name} onChange={handleField('org_name')} placeholder="Acme Corp" />
            <Field label="Org Code" value={form.org_code} onChange={handleField('org_code')} placeholder="ACME" />
            <Field label="Country (ISO-2)" value={form.country} onChange={handleField('country')} placeholder="US" />
            <SelectField label="Currency" value={form.default_currency} onChange={handleField('default_currency')}
              options={['USD', 'EUR', 'GBP', 'BRL', 'SAR', 'AED', 'JPY']} />
            <SelectField label="Framework" value={form.default_framework} onChange={handleField('default_framework')}
              options={['US_GAAP', 'IFRS', 'BR_GAAP', 'SOCPA']} />
            <Field label="Timezone" value={form.default_timezone} onChange={handleField('default_timezone')} placeholder="UTC" />
            <SelectField label="Tier" value={form.tier} onChange={handleField('tier')}
              options={['FREE', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE']} />
            <Field label="Primary Contact Email" value={form.primary_contact_email} onChange={handleField('primary_contact_email')} placeholder="ops@acme.com" />
            <Field label="Primary Contact Name" value={form.primary_contact_name} onChange={handleField('primary_contact_name')} placeholder="Jane Doe" />
          </div>

          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text-primary)' }}>
              Bootstrap Admin User (optional)
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 12 }}>
              Create an L7/ADMIN user at the same time. They can log in and continue onboarding.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <Field label="Admin Email" value={form.admin_email} onChange={handleField('admin_email')} placeholder="admin@acme.com" />
              <Field label="Admin Full Name" value={form.admin_full_name} onChange={handleField('admin_full_name')} placeholder="Admin User" />
              <Field label="Admin Password (min 8)" value={form.admin_password} onChange={handleField('admin_password')} type="password" placeholder="••••••••" />
            </div>
          </div>

          <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setShowForm(false); setForm(initialForm); setFormError(null); }}
              style={{ padding: '8px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#fff', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}
            >
              Cancel
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              style={{ padding: '8px 18px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', background: '#1e3a5f', border: 'none', color: '#fff', opacity: submitting ? 0.7 : 1 }}
            >
              {submitting ? 'Creating...' : 'Create Tenant'}
            </button>
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={orgs}
        loading={loading}
        emptyMessage="No tenants yet. Click 'New Tenant' to create one."
        rowKey="id"
      />
    </PageLayout>
  );
}

function Field({ label, value, onChange, placeholder, type }: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>{label}</span>
      <input
        type={type || 'text'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: string[];
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-hint)', letterSpacing: '0.04em' }}>{label}</span>
      <select
        value={value}
        onChange={onChange}
        style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-default)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
